import React, { FC, useState, useEffect } from "react";
import { Table, Select, InputNumber, Space, Empty, DatePicker } from "antd";
import Icon, {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Alert, Button, Spinner } from "reactstrap";
import { ReactTyped } from "react-typed";
import { MaterialOrService, ProductionSchedule } from "../../store/slices/tender/TenderList";
import dayjs from "dayjs";

interface ProductionScheduleRow extends ProductionSchedule {
  isEditing?: boolean;
}

const EditProductionScheduleTable: FC<{
  onSave: any;
  list: ProductionSchedule[];
  lineNo: number;
  onCancel: () => void;
}> = ({  list, onCancel, onSave, lineNo }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    status: "success" | "danger" | undefined;
    message: string;
  }>({
    status: undefined,
    message: "",
  });
  const [data, setData] = useState<ProductionScheduleRow[]>(list || []);
  const [editingKey, setEditingKey] = useState<string | number | null>(null);
  const [originalRow, setOriginalRow] = useState<ProductionScheduleRow | null>(
    null
  );
  const [initialKeys, setInitialKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setInitialKeys(new Set(list?.map((item) => item.key)));
  }, [list]);

  const isEditing = (record: ProductionScheduleRow) =>
    record.key === editingKey;

  const handleEdit = (key: string | number) => {
    const row = data.find((item) => item.key === key);
    if (row) {
      setOriginalRow({ ...row });
      setEditingKey(key);
    }
  };

  const handleCancel = (key: string | number) => {
    const isNew = key?.toString()?.startsWith("new-");
    if (isNew && !originalRow?.key) {
      handleDelete(key);
      setOriginalRow(null);
      return;
    }
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, ...originalRow } : item
      )
    );
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleSave = (key: string | number) => {
    const row = data.find((item) => item.key == key);
    if (!row) return;

    if (
      !row.productionLeadDays ||
      !row.productionQuantity ||
      !row.plannedStartDate
    ) {
      setAlertMessage({
        status: "danger",
        message: "Please fill all fields before saving.",
      });
      setTimeout(
        () => setAlertMessage({ status: undefined, message: "" }),
        4000
      );
      return;
    }
    setEditingKey(null);
    setOriginalRow(null);
  };

  const handleDelete = (key: string | number) => {
    setData((prev) => prev.filter((item) => item.key !== key));
    if (editingKey === key) {
      setEditingKey(null);
    }
  };

  const handleAdd = () => {
    if (editingKey) return;

    const newKey = `new-${Date.now()}`;
    setData((prev: ProductionScheduleRow[]) => [
      ...prev,
      {
        key: newKey,
        plannedStartDate: "",
        plannedEndDate: "",
        productionQuantity: 0,
        productionLeadDays: 0,
        isEditing: true,
      },
    ]);
    setEditingKey(newKey);
  };

  const handleFinalSave = () => {
    setIsSaving(true);


    onSave((materials: MaterialOrService[]) => {
      return materials.map((material: any) => {
        if (material?.lineNo == lineNo) {
          return {
            ...material,
            _importError: null,
            productionSchedules:data?.map((item) => ({
              key: item?.key,
              productionQuantity: item?.productionQuantity,
              productionLeadDays: item?.productionLeadDays,
              plannedStartDate: item?.plannedStartDate,
              plannedEndDate: item?.plannedEndDate
            })),
          };
        } else return material;
      });
    });
    onCancel();
  };

  const handleChange = (
    key: string,
    field: keyof ProductionScheduleRow,
    value: any
  ) => {
    setData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const configColumns = (data: ProductionScheduleRow[]) => {
    return [
      {
        title: "Production Quantity",
        dataIndex: "productionQuantity",
        key: "productionQuantity",
        render: (_: any, record: ProductionScheduleRow) =>
          isEditing(record) ? (
            <InputNumber
              value={record.productionQuantity}
              style={{ width: "100%" }}
              step={1}
              parser={(value: any) => parseInt(value, 10)}
              min={-1000}
              max={1000}
              onChange={(value) =>
                handleChange(record.key, "productionQuantity", value ?? 0)
              }
            />
          ) : (
            record.productionQuantity
          ),
      },
      {
        title: "Planned Start Date",
        dataIndex: "plannedStartDate",
        key: "plannedStartDate",
        render: (_: any, record: ProductionScheduleRow) =>
          isEditing(record) ? (
            <DatePicker
              value={
                record.plannedStartDate ? dayjs(record.plannedStartDate) : null
              }
              format="DD/MMM/YYYY"
              placeholder="Select Date"
              onChange={(date, dateString) =>
                handleChange(record.key, "plannedStartDate", dateString)
              }
            />
          ) : (
            record.plannedStartDate
          ),
      },
      {
        title: "Lag Days",
        dataIndex: "lagDays",
        key: "lagDays",
        width: data?.length == 0 ? 120 : 80,
        render: (_: any, record: ProductionScheduleRow) =>
          isEditing(record) ? (
            <InputNumber
              value={record.productionLeadDays}
              style={{ width: "100%" }}
              step={1}
              parser={(value: any) => parseInt(value, 10)}
              min={-1000}
              max={1000}
              onChange={(value) =>
                handleChange(record.key, "productionLeadDays", value ?? 0)
              }
            />
          ) : (
            record.productionLeadDays
          ),
      },
      {
        title: "Planned End Date",
        dataIndex: "plannedEndDate",
        key: "plannedEndDate",
        render: (_: any, record: ProductionScheduleRow) => {
          const plannedEndDate =
            record.plannedStartDate && record.productionLeadDays
              ? dayjs(record.plannedStartDate)
                  .add(record.productionLeadDays, "days")
                  .format("DD/MMM/YYYY")
              : "";
          return isEditing(record) ? (
            <span>{plannedEndDate}</span>
          ) : (
            plannedEndDate
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        hidden: data?.length == 0,
        width: 100,
        render: (_: any, record: ProductionScheduleRow) => {
          const editable = isEditing(record);
          return editable ? (
            <Space>
              <Button
                type="button"
                className="btn-dim btn btn-secondary px-2 py-1"
                onClick={() => handleSave(record.key)}
              >
                <SaveOutlined />
              </Button>
              <Button
                type="button"
                className="btn-dim btn btn-secondary px-2 py-1"
                onClick={() => handleCancel(record.key)}
              >
                <CloseOutlined />
              </Button>
            </Space>
          ) : (
            <Space>
              <Button
                className="btn-dim btn btn-secondary px-2 py-1"
                type="button"
                disabled={!!editingKey}
                onClick={() => handleEdit(record.key)}
              >
                <EditOutlined />
              </Button>
              <Button
                onClick={() => handleDelete(record.key)}
                className="btn-dim btn btn-secondary px-2 py-1"
                type="button"
                danger
                disabled={!!editingKey}
              >
                <DeleteOutlined />
              </Button>
            </Space>
          );
        },
      },
    ];
  };

  return (
    <div>
      <Table
        className="customTable"
        columns={configColumns(data)}
        dataSource={data}
        pagination={false}
        bordered
        rowKey="key"
        locale={{
          emptyText: (
            <>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <>
                    <strong>
                      No Schedule Found, Please{" "}
                      <a
                        onClick={handleAdd}
                        className="primary"
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                        }}
                      >
                        Add Schedule.
                      </a>
                    </strong>
                  </>
                }
              />
            </>
          ),
        }}
      />
      {data?.length != 0 && !alertMessage?.status && (
        <Button
          type="button"
          className="btn-dim btn btn-secondary d-flex align-items-center justify-content-center"
          onClick={handleAdd}
          style={{ marginTop: 16 }}
          block
          disabled={!!editingKey}
        >
          <PlusOutlined />
          &nbsp;
          <span> Add Schedule</span>
        </Button>
      )}
      {!!alertMessage?.status ? (
        <Alert
          isOpen
          color={alertMessage?.status}
          className="start-0 w-100 py-1 mt-2"
          style={{
            top: 0,
            textAlign: "left",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          <Icon name={"info"} style={{ top: "0.6rem" }} />
          <ReactTyped
            strings={[alertMessage?.message || ""]}
            typeSpeed={10}
            showCursor={false}
          />
        </Alert>
      ) : (
        <></>
      )}
      {!alertMessage?.status ? (
        <div className="actions clearfix mt-3">
          <ul>
            <li>
              {isSaving ? (
                <Button color="primary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  disabled={
                    !!editingKey || (list?.length == 0 && data?.length == 0)
                  }
                  onClick={handleFinalSave}
                >
                  Save
                </Button>
              )}
            </li>
            <li>
              <Button
                size="sm"
                color="primary"
                className="btn-dim btn btn-secondary"
                type="button"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default EditProductionScheduleTable;
